import axios from "axios";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/slices/loadingSlice";
import store from "../../redux/store";
import { showToast } from "../../redux/slices/toastSlice";
import { removeToken } from "../../redux/slices/loginSlice";
import { toastIgnore } from "./toastIgnore";

const BASE_URL = "https://api.7-trade.ir/api";
const TIMEOUT = 600000;

axios.defaults.baseURL = BASE_URL;
axios.defaults.timeout = TIMEOUT;

// let expiration = moment(JSON.parse(localStorage.getItem("sayahUser"))?.expiration);
// let current = moment();

// if (expiration <= current) {
//   await store.dispatch(setLogoutAction());
// }

axios.defaults.headers.common["Authorization"] = `Bearer ${
  localStorage.getItem("token") || null
}`;


const requestOnFullFilled = (config) => {
  if (config.method && config.method.toLowerCase() === "get") {
    store.dispatch(showLoading());
  }
  return config;
};

const requestOnRejected = (error) => {
  if (
    error.config &&
    error.config.method &&
    error.config.method.toLowerCase() === "get"
  ) {
    store.dispatch(hideLoading());
  }
  return Promise.reject(error);
};

const responseOnRejected = async (error) => {
  store.dispatch(hideLoading());
  console.error("API Error:", error);

  if (!error.response) {
    if (
      error.code === "NETWORK_ERR" ||
      error.message.includes("Network Error")
    ) {
      store.dispatch(
        showToast({
          message: "Please check your internet connection.",
          type: "error",
        })
      );
    } else if (
      error.code === "ECONNABORTED" ||
      error.message.includes("timeout")
    ) {
      store.dispatch(
        showToast({
          message: "The request timed out. Please try again.",
          type: "error",
        })
      );
    } else {
      store.dispatch(
        showToast({
          message: "Unable to connect to the server.",
          type: "error",
        })
      );
    }
    throw error;
  }

  const { status, data } = error.response;

  switch (status) {
    case 400:
      store.dispatch(
        showToast({
          message: data?.message || "Invalid request.",
          type: "error",
        })
      );
      break;

    case 401:
      console.warn("Unauthorized - Logging out...");
      store.dispatch(removeToken());
      console.log("User token removed from store.");

      store.dispatch(
        showToast({
          message: "Your session has expired. Please log in again.",
          type: "error",
        })
      );

      setTimeout(() => {
        window.location.href = "/login";
      }, 500);
      break;

    case 403:
      console.warn("Forbidden - Access denied");
      store.dispatch(
        showToast({
          message: "You do not have permission to access this section.",
          type: "error",
        })
      );
      break;

    case 404:
      store.dispatch(
        showToast({
          message: "The requested page or information was not found.",
          type: "error",
        })
      );
      break;

    case 422:
      store.dispatch(
        showToast({
          message: data?.message || "The submitted information is invalid.",
          type: "error",
        })
      );
      break;

    case 429:
      store.dispatch(
        showToast({
          message: "Too many requests. Please wait a moment and try again.",
          type: "error",
        })
      );
      break;

    case 500:
      store.dispatch(
        showToast({
          message: "Internal server error. Please try again later.",
          type: "error",
        })
      );
      break;

    case 502:
    case 503:
      store.dispatch(
        showToast({
          message: "The server is currently unavailable.",
          type: "error",
        })
      );
      break;

    case 504:
      store.dispatch(
        showToast({
          message: "The server did not respond. Please try again.",
          type: "error",
        })
      );
      break;

    default:
      store.dispatch(
        showToast({
          message: data?.message || `An unexpected error occurred (Code: ${status}).`,
          type: "error",
        })
      );
  }

  throw error;
};

axios.interceptors.request.use(requestOnFullFilled, requestOnRejected);

axios.interceptors.response.use(undefined, responseOnRejected);

const isMutatingMethod = (method) => {
  if (!method) return false;
  const m = String(method).toLowerCase();
  return m === "post" || m === "put" || m === "delete" || m == "patch";
};

const extractMessage = (obj) => {
  return (
    obj?.response?.data?.data?.en_message ||
    obj?.data?.data?.en_message ||
    obj?.en_message ||
    obj?.data?.en_message ||
    obj?.response?.data?.en_message ||
    obj?.response?.en_message ||
    obj?.data?.en_message ||
"An error has occurred!"
  );
};

const responseBody = (response) => {
  try {
    const method = response?.config?.method;
    if (toastIgnore.includes(response?.config?.url)) {
      return response;
    }
    if (isMutatingMethod(method)) {
      const message =   extractMessage(response) || "Operation completed successfully";

      store.dispatch(showToast({ type: "success", message }));
    }
  } catch (e) {
    console.error("toast responseBody error:", e);
  } finally {
    store.dispatch(hideLoading());
  }

  return response;
};

const errorBody = (error) => {
  try {
    const method =
      error?.config?.method ||
      error?.response?.config?.method ||
      error?.request?.method;
    
    // بررسی آدرس برای ignore کردن نوتیفیکیشن
    const shouldIgnore =
      toastIgnore.includes(error?.config?.url) ||
      toastIgnore.includes(error?.response?.config?.url);
    
    if (shouldIgnore) {
      console.log("⚠️ Error caught but notification ignored (toastIgnore)");
      return Promise.reject(error);
    }
    
    if (isMutatingMethod(method)) {
      const message = extractMessage(error) || "Something went wrong while performing the operation";
      store.dispatch(showToast({ type: "error", message }));
    }
  } catch (e) {
    console.error("❌ Exception in errorBody:", e);
  } finally {
    store.dispatch(hideLoading());
  }
  
  throw error?.response || error;
};

const requests = {
  get: (url) => axios.get(url).then(responseBody).catch(errorBody),
  getByParams: (url, params) =>
    axios.get(url, { params }).then(responseBody).catch(errorBody),
  post: (url, body) =>
    axios.post(url, body).then(responseBody).catch(errorBody),
  put: (url, body) => axios.put(url, body).then(responseBody).catch(errorBody),
  patch: (url, body) =>
    axios.patch(url, body).then(responseBody).catch(errorBody),
  delete: (url) => axios.delete(url).then(responseBody),
  postFormData: (url, formData) => {
    return axios
      .post(url, formData, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody);
  },
  putMedia: (url, body) => {
    return axios
      .put(url, body, {
        headers: {
          "Content-type": "application/x-www-form-urlencoded",
        },
      })
      .then(responseBody)
      .catch(errorBody);
  },
};

export default requests;
